import { Colors } from '../types';
import { generalColors as legacyGeneralColors } from '../brand2020/colors';
import { themeTokens } from './themeTokens';

const cedar = '#00A68A';
const jade = '#42D49C';
const jade40 = 'rgba(66, 212, 156, 0.4)';
const lake = '#004963';
const mint = '#A4FFB8';
const nocturn = '#012B3A';
const nocturn10 = 'rgba(1, 43, 58, 0.1)';
const nocturn75 = 'rgba(1, 43, 58, 0.75)';
const saffron = '#FF4C46';
const saffron40 = 'rgba(255,76,70, 0.4)';
const spring = '#87F8AE';
const spruce = '#007978';
const tea = '#DFFFDE';
const white = '#FFFFFF';
const text = '#241F21';

const inputGreyText = '#7B797A';
const inputGreyBorder = '#979797';

const steppedProgressBarBackground = '#F0F0F0';

const twitter = '#1DA1F2';
const facebook = '#1877f2';
const google = '#ea4335';
const linkedin = '#0a66c2';
const youtube = '#FF0000';
const instagram = '#d93175';
const flickr = '#1057ae';
const tumblr = '#001935';
const pinterest = '#E60023';
const tiktok = '#241F21';

const darkGrey = '#1C1C1C';
const lightGray = '#FDFDFD';
const lightGray20 = '#EEF1F2';
const disabledGray = '#5C5C5C';
const disabledBorder = '#F4F5F6';
const focusBorder = '#0065FF';
const activeGrey = '#E6EAEB';

export const generalColors = {
  ...legacyGeneralColors,
  cedar,
  jade,
  lake,
  mint,
  nocturn,
  saffron,
  spring,
  spruce,
  tea,
  white,
  text,
};

const checkbox = {
  text: nocturn,
  border: jade,
  background: legacyGeneralColors.lightGrey10,
  tick: legacyGeneralColors.primary,
  disabledBorder: legacyGeneralColors.darkGrey20,
  disabledText: legacyGeneralColors.darkGrey40,
  disabledBackground: legacyGeneralColors.lightGrey40,
  disabledTick: legacyGeneralColors.darkGrey40,
};

export const specificColors = {
  /**
   * Applicable to any component.
   */
  focusBorder,
  errorBorder: saffron,

  button: {
    /**
     * Applicable to any button.
     */
    text: themeTokens.text.onPrimary,
    background: themeTokens.bg.primary,
    disabledText: themeTokens.text.disabled,
    disabledBackground: themeTokens.bg.disabled,
    activeBackground: themeTokens.bg.primaryPressed,
    focusBackground: themeTokens.bg.primary,
    hoverBackground: themeTokens.bg.primaryHovered,

    lightBackground: 'rgba(252, 252, 251, 0.6)',
    disabledLightBackground: 'rgba(252, 252, 251, 0.2)',
    activeLightBackground: 'rgba(252, 252, 251, 0.9)',
    focusLightBackground: 'rgba(252, 252, 251, 0.9)',
    hoverLightBackground: 'rgba(252, 252, 251, 0.8)',

    secondary: {
      text: themeTokens.text.onSecondary,
      background: themeTokens.bg.secondary,
      border: themeTokens.bg.secondary,
      hoverText: themeTokens.text.onSecondary,
      hoverBackground: themeTokens.bg.secondaryHovered,
      hoverBorder: themeTokens.bg.secondaryHovered,
      activeText: themeTokens.text.onPrimary,
      activeBackground: themeTokens.bg.secondaryActive,
      focusBackground: themeTokens.bg.secondary,
      disabledBorder: themeTokens.bg.disabled,
      disabledBackground: themeTokens.bg.disabled,
    },

    outlined: {
      text: darkGrey,
      border: darkGrey,
      hoverText: darkGrey,
      hoverBorder: darkGrey,
      background: lightGray,
      disabledBackground: lightGray,
      hoverBackground: lightGray20,
      activeBackground: activeGrey,
      focusBackground: lightGray20,
      disabledText: disabledGray,
      disabledBorder: disabledBorder,
    },

    onlyText: {
      text: darkGrey,
      hoverText: darkGrey,
      background: lightGray,
      disabledBackground: lightGray,
      hoverBackground: lightGray20,
      activeBackground: lightGray20,
      focusBackground: lightGray20,
      disabledText: disabledGray,
    },

    cta: {
      background: legacyGeneralColors.accent,
      disabledBackground: legacyGeneralColors.accent10,
      activeBackground: legacyGeneralColors.accent80,
      focusBackground: legacyGeneralColors.accent60,
      hoverBackground: legacyGeneralColors.accent60,
    },
  },

  icon: {
    success: jade,
    error: saffron,
  },

  input: {
    text: nocturn,
    background: white,
    border: inputGreyBorder,
    disabledText: inputGreyText,
    disabledBackground: nocturn10,
    disabledBorder: nocturn,
    errorBackground: saffron,
    placeholderText: inputGreyText,
  },

  portfolioNav: {
    background: legacyGeneralColors.lightGrey60,
  },

  masthead: {
    background: white,
  },

  modal: {
    background: legacyGeneralColors.lightGrey10,
    footerBackground: legacyGeneralColors.lightGrey20,
  },

  popover: {
    background: legacyGeneralColors.lightGrey10,
    border: legacyGeneralColors.complementaryPurple,
    success: generalColors.jade,
    error: generalColors.saffron,
  },

  overlay: {
    background: nocturn75,
  },

  toast: {
    error: {
      background: themeTokens.bg.negative,
      iconBackground: saffron40,
      icon: themeTokens.icon.negative,
    },
    warning: {
      background: themeTokens.bg.warning,
      iconBackground: legacyGeneralColors.accent40,
      icon: themeTokens.icon.warning,
    },
    success: {
      background: themeTokens.bg.positive,
      iconBackground: jade40,
      icon: themeTokens.icon.positive,
    },
    info: {
      background: themeTokens.bg.info,
      iconBackground: legacyGeneralColors.secondary40,
      icon: themeTokens.icon.info,
    },
  },

  dropdownMenu: {
    border: legacyGeneralColors.complementaryPurple,
    background: legacyGeneralColors.lightGrey10,
  },

  menuItem: {
    text: legacyGeneralColors.darkGrey,
    disabledText: legacyGeneralColors.darkGrey40,
    activeBackground: legacyGeneralColors.accent40,
    hoverBackground: legacyGeneralColors.accent10,
    focusBackground: legacyGeneralColors.accent10,
  },

  tooltip: {
    text: legacyGeneralColors.lightGrey10,
    background: legacyGeneralColors.darkGrey,
  },

  ribbon: {
    background: generalColors.jade,
  },

  hyperlink: {
    color: themeTokens.text.hyperlink,
    hoverColor: mint,
    alt: {
      color: lake,
      hoverColor: spruce,
    },
  },

  tab: {
    selectedBorder: legacyGeneralColors.primary,
    hoverBorder: legacyGeneralColors.primary60,
  },

  checkbox,
  radioButton: checkbox,

  toggleSwitch: {
    onBackground: legacyGeneralColors.complementaryPurple,
    offBackground: legacyGeneralColors.darkGrey40,
  },

  tag: {
    // aka selection pill
    text: legacyGeneralColors.primary,
    background: legacyGeneralColors.complementaryOrange20,
    disabledText: legacyGeneralColors.primary40,
    disabledBackground: legacyGeneralColors.complementaryOrange10,
    activeBackground: legacyGeneralColors.complementaryOrange60,
    focusBackground: legacyGeneralColors.complementaryOrange40,
    hoverBackground: legacyGeneralColors.complementaryOrange40,
  },

  card: {
    background: legacyGeneralColors.lightGrey10,
    selectedBackground: legacyGeneralColors.accent10,
  },

  steppedProgressBar: {
    background: steppedProgressBarBackground,
    foreground: jade,
  },

  genericBox: {
    background: lake,
  },

  socialMedia: {
    twitter,
    facebook,
    google,
    linkedin,
    youtube,
    instagram,
    flickr,
    tumblr,
    pinterest,
    tiktok,
  },

  text: {
    success: jade,
    error: saffron,
  },

  banner: {
    success: {
      background: cedar,
    },
  },
};

export const colors: Colors = {
  ...generalColors,
  ...specificColors,
};
